import { useState, useEffect, useCallback } from 'react';
import { request, gql } from 'graphql-request';
import { Box, Wrap, WrapItem, Heading, Image, Text, HStack } from '@chakra-ui/react';
import type { WindowPanelProps } from '../../../types/window';
import { WindowFrame } from '../../../components/window';
import './styles.css';

const graphcms = 'https://api-ap-south-1.graphcms.com/v2/cl4mpujwl7y9a01xv80jxacfs/master';

const QUERY = gql`
  {
    projects {
      title
      tags
      description
      projectImage {
        url
      }
      projectLink
    }
  }
`;

interface ProjectImage {
  url: string;
}

interface Project {
  title: string;
  tags: string[];
  description: string;
  projectImage: ProjectImage;
  projectLink: string;
}

interface ProjectsQueryResult {
  projects: Project[];
}

interface ProjectCardProps {
  title: string;
  tags: string[];
  desc: string;
  image: string;
}

function ProjectCard({ title, tags, desc, image }: ProjectCardProps) {
  return (
    <Box
      p={5}
      bg="#0C0C0C"
      color="white"
      shadow="md"
      border="1px solid #647D87"
      borderRadius="4px"
      minWidth="200px"
      maxWidth="300px"
    >
      <Image h="200px" w="100%" objectFit="cover" src={image} alt={title} />
      <HStack paddingInline={8} paddingBlock={4}>
        <Wrap>
          {tags.map((data, index) => (
            <WrapItem key={index}>
              <Box
                border="1px solid #00bfa5"
                borderRadius="4px"
                paddingInline={12}
                paddingBlock={6}
              >
                <Text m={0} fontSize="16px" fontFamily="WotfardSemi" color="#00bfa5">
                  {data}
                </Text>
              </Box>
            </WrapItem>
          ))}
        </Wrap>
      </HStack>
      <Heading fontSize="xl" p={4} color="#e9ebf1">
        {title}
      </Heading>
      <Text mt={4} p={4} fontSize="16px" fontFamily="WotfardRegular" color="#cbcedd">
        {desc}
      </Text>
    </Box>
  );
}

const Projects = (props: WindowPanelProps) => {
  const [projects, setProjects] = useState<Project[] | null>(null);

  useEffect(() => {
    const fetchProjects = async () => {
      const { projects: fetchedProjects } = await request<ProjectsQueryResult>(graphcms, QUERY);
      setProjects(fetchedProjects);
    };

    void fetchProjects();
  }, []);

  const handleClose = useCallback(() => {
    props.parentCallback(false, 'showHideProjects');
  }, [props]);

  const handleFocus = useCallback(() => {
    props.parentCallbackTwo(0, 1, 0, 0, 0);
  }, [props]);

  return (
    <WindowFrame
      title="PROJECTS"
      windowId="projects_window"
      zIndex={props.data}
      theme="dark"
      sizePreset="lg"
      onClose={handleClose}
      onFocus={handleFocus}
      contentClassName="projects-window-content"
      contentStyle={{
        background: `repeating-linear-gradient(
          0deg,
          transparent,
          transparent 19px,
          rgba(0, 191, 165, 0.2) 20px
        ),
        repeating-linear-gradient(
          90deg,
          transparent,
          transparent 19px,
          rgba(0, 191, 165, 0.2) 20px
        )`,
        backgroundColor: '#000000',
      }}
    >
      {!projects ? (
        <Text color="white" p={4}>
          Loading
        </Text>
      ) : (
        <Wrap spacing="20px" p="20px">
          {projects.map(
            ({ title, tags, description, projectImage, projectLink: _projectLink }, index) => (
              <WrapItem key={index}>
                <ProjectCard
                  title={title}
                  tags={tags}
                  desc={description}
                  image={projectImage.url}
                />
              </WrapItem>
            ),
          )}
        </Wrap>
      )}
    </WindowFrame>
  );
};

export default Projects;
